// core-js includes polyfills to get the app working on Internet Explorer
import 'core-js';
// whatwg-fetch is a window.fetch polyfill needed for fetch to work in older verisons of Internet Explorer
import 'whatwg-fetch';
import React, { useEffect, useRef, useState } from 'react';
import './index.css';
import App from './App';
import MobileApp from './MobileApp';
import OnboardingLayer from './components/OnboardingLayer';
import MissionsTutorialLayer from './components/MissionsTutorialLayer';
import AppInit from './components/AppInit';
import SocialLoginInterface from 'shared/api/SocialLoginInterface';
import ENVIRONMENT_VARIABLES from 'shared/utils/environmentVariables';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/lib/integration/react';
import { persistor, store } from 'shared/state/store';
import { setIsConnected } from 'shared/state/actions/device';
import { setAdBlocker } from 'shared/state/actions/apiRoot';
import { isMobile, isTablet } from 'react-device-detect';
import { ErrorBoundary } from './components/ErrorBoundary';
import { themes } from 'shared/context/Themes';
import { NotificationProvider } from './context/NotificationProvider';
import { handleHtmlScripts } from './prependScripts';
import LoadingLayer from './components/Loading/Loading';
import RotateScreen from './screens/RotateScreen/RotateScreen';
import { isMobileSupported, isNotStandaloneMobilePWA } from './utils/mobileSupported';
import { ThemeProvider, StyleSheetManager } from 'styled-components';
import isPropValid from '@emotion/is-prop-valid';
import useCheckOrientation from './hooks/useCheckOrientation';
import useViewport from './hooks/useViewport';
import useMediaMetadata from './hooks/useMediaMetadata';
import BrowserScreen from './screens/BrowserScreen/BrowserScreen';
import CrossPlatformPWAPrompt from './components/CrossPlatformPwaPrompt/CrossPlatformPwaPrompt';

const shouldForwardProp = (prop, target) => (typeof target === 'string' ? isPropValid(prop) : true);

const RotationScreenWrapper = () => (
  <>
    <CrossPlatformPWAPrompt />
    <RotateScreen />
  </>
);

const MainApplication = () => (
  <PersistGate loading={<div>Loading...</div>} persistor={persistor}>
    <ErrorBoundary>
      <AppInit>
        <App />
        <OnboardingLayer />
        <MissionsTutorialLayer />
        <LoadingLayer />
      </AppInit>
    </ErrorBoundary>
  </PersistGate>
);

const Redux = () => {
  useViewport();
  useMediaMetadata();
  const { isPortrait, isLandscape } = useCheckOrientation();

  const [firstRender, setFirstRender] = useState(true);
  const [orientationLoading, setOrientationLoading] = useState(false);
  const [showBrowserMobileScreen, setShowBrowserMobileScreen] = useState(true);
  const willMount = useRef(true);

  const mobileSupported = isMobile && isMobileSupported();
  const showPortraitRotateScreen = mobileSupported && isPortrait;
  const showRotateScreenOnFirstRender = showPortraitRotateScreen && firstRender;
  const showMobileAppNotSupported = isMobile && !isMobileSupported();
  const shouldShowBrowserScreen =
    (isNotStandaloneMobilePWA || (isTablet && isLandscape && isNotStandaloneMobilePWA)) && showBrowserMobileScreen;

  useEffect(() => {
    const handleOrientationChange = () => {
      setOrientationLoading(true);
      setTimeout(() => {
        setOrientationLoading(false);
      }, 1000);
    };

    const mediaQuery = window.matchMedia('(orientation: landscape)');
    mediaQuery.addEventListener('change', handleOrientationChange);

    return () => {
      mediaQuery.removeEventListener('change', handleOrientationChange);
    };
  }, []);

  const checkToLoadScripts = () => {
    if (willMount.current) {
      handleHtmlScripts();
      detectAdBlock();
    }
    willMount.current = false;
  };

  checkToLoadScripts();

  useEffect(() => {
    const timer = setTimeout(() => {
      !window.isGsiLoaded && SocialLoginInterface.loadGsi();
    }, 1500);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (firstRender && isLandscape) {
      setFirstRender(false);
    }
  }, [firstRender, isLandscape]);

  const renderContent = () => {
    if (showMobileAppNotSupported) {
      return <MobileApp />;
    }

    if (showRotateScreenOnFirstRender) {
      return <RotationScreenWrapper />;
    }
    if (showPortraitRotateScreen) {
      return <RotationScreenWrapper />;
    }

    if (shouldShowBrowserScreen) {
      return <BrowserScreen setShowBrowserMobileScreen={setShowBrowserMobileScreen} />;
    }
    return <MainApplication />;
  };

  return (
    <Provider store={store}>
      <StyleSheetManager shouldForwardProp={shouldForwardProp}>
        <ThemeProvider theme={themes[ENVIRONMENT_VARIABLES.APPLICATION_TARGET]}>
          <NotificationProvider>
            {orientationLoading && <LoadingLayer loading={orientationLoading} transparency={1} />}
            {renderContent()}
          </NotificationProvider>
        </ThemeProvider>
      </StyleSheetManager>
    </Provider>
  );
};

window.addEventListener('offline', () => {
  store.dispatch(setIsConnected(false));
});

window.addEventListener('online', () => {
  store.dispatch(setIsConnected(true));
});

async function detectAdBlock() {
  let adBlockEnabled = false;
  const googleAdUrl = ENVIRONMENT_VARIABLES.GOOGLE_AD_URL;

  try {
    await fetch(new Request(googleAdUrl)).catch(() => (adBlockEnabled = true));
  } catch (e) {
    adBlockEnabled = true;
  } finally {
    store.dispatch(setAdBlocker(adBlockEnabled));
  }
}

export default Redux;
